import {
  ANCHOR_PRICE_USD,
  FOUNDING_OFFER_END,
  REFUND_GRACE_DAYS,
  freeMockSeoDescription,
  currentPassPriceUsd,
  getDynamicPricing,
} from '../constants/pricing';
import { COMPANY_LEGAL_NAME } from '../constants/company';
import { SUPPORT_EMAIL } from '../constants/contact';

import { canonicalUrl, SITE } from '../seo/urls';

export { SITE };
export const ORG_ID = `${SITE}/#organization`;
const PRICING_URL = canonicalUrl('/pricing');
const PRODUCT_IMAGE = `${SITE}/og.png`;

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'CFA Mock Exam',
    legalName: COMPANY_LEGAL_NAME,
    email: SUPPORT_EMAIL,
    url: `${SITE}/`,
    logo: `${SITE}/logo512.png`,
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE}/#website`,
    name: 'CFA Mock Exam',
    alternateName: ['CFAMockExam', 'cfamockexam.com'],
    url: `${SITE}/`,
    publisher: { '@id': ORG_ID },
    inLanguage: 'en',
  };
}

function digitalDeliveryDetails() {
  return {
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: 'US',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: REFUND_GRACE_DAYS,
      returnMethod: 'https://schema.org/ReturnByMail',
      refundType: 'https://schema.org/FullRefund',
      returnFees: 'https://schema.org/FreeReturn',
    },
    shippingDetails: {
      '@type': 'OfferShippingDetails',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency: 'USD',
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: 'US',
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 0,
          maxValue: 0,
          unitCode: 'DAY',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 0,
          maxValue: 0,
          unitCode: 'DAY',
        },
      },
    },
  };
}

// Keep in sync with scripts/seo-schema.mjs; all dates use UTC calendar days.
function offerPriceValidUntil(now) {
  const pricing = getDynamicPricing(now);
  if (pricing.active) {
    return pricing.flash
      ? new Date(pricing.flash.endsAt).toISOString().slice(0, 10)
      : pricing.tierUntil || FOUNDING_OFFER_END;
  }
  return `${now.getUTCFullYear() + 1}-12-31`;
}

function lifetimeAccessOffer({ priceUsd, url = PRICING_URL, now = new Date() }) {
  return {
    '@type': 'Offer',
    url,
    price: String(priceUsd),
    priceCurrency: 'USD',
    validFrom: '2026-08-01',
    priceValidUntil: offerPriceValidUntil(now),
    availability: 'https://schema.org/InStock',
    description: `Level 1 lifetime access — eight full timed mocks, one-time payment (list $${ANCHOR_PRICE_USD})`,
  };
}

/** Homepage: app entry point — paid tier linked to /pricing, not a $0 product. */
export function homeWebApplicationSchema(now = new Date()) {
  const priceUsd = currentPassPriceUsd(now);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    '@id': `${SITE}/#webapp`,
    name: 'CFA Mock Exam',
    url: `${SITE}/`,
    applicationCategory: 'EducationalApplication',
    description: freeMockSeoDescription(),
    offers: lifetimeAccessOffer({ priceUsd, url: PRICING_URL, now }),
    publisher: { '@id': ORG_ID },
  };
}

/** Pricing page: Product + Offer at the current price. */
export function pricingProductSchema(now = new Date()) {
  const priceUsd = currentPassPriceUsd(now);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${PRICING_URL}#product`,
    name: 'CFA Level 1 Lifetime Access',
    description:
      'One-time payment for lifetime access: eight weighted 90-question timed mocks, topic breakdown, post-mock analysis, explanations, and topic drills from a 720+ bank.',
    url: PRICING_URL,
    image: [PRODUCT_IMAGE, `${SITE}/logo512.png`],
    brand: { '@type': 'Brand', name: 'CFA Mock Exam' },
    category: 'Educational software',
    offers: {
      ...lifetimeAccessOffer({ priceUsd, url: `${PRICING_URL}#purchase`, now }),
      ...digitalDeliveryDetails(),
      seller: { '@id': ORG_ID },
    },
  };
}
